import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DeviceClassRule, Obligation } from '@regigov/contracts';
import { obligationsApi } from '../api/compliance';
import { rulesApi } from '../api/devices';
import { useAuth } from '../auth/AuthContext';
import { isCerAdminRole } from '../auth/roles';
import { Field, ProblemAlert, formatDateTime } from './ui';

const ruleLabel = (r: { deviceClass: string; version: number; validFrom: string; validTo: string | null }) => `${r.deviceClass} v${r.version} (${r.validFrom} → ${r.validTo ?? 'open'})`;

/** CER obligations: every version of every obligation; CER admin publishes new obligations and versions. */
export function ObligationsPage() {
  const { user } = useAuth();
  const canConfigure = isCerAdminRole(user?.roles);
  const obligations = useQuery({ queryKey: ['obligations'], queryFn: obligationsApi.list });
  const [editing, setEditing] = useState<Obligation | 'new' | null>(null);

  const byCode = new Map<string, Obligation[]>();
  for (const o of obligations.data?.items ?? []) byCode.set(o.code, [...(byCode.get(o.code) ?? []), o]);

  return (
    <>
      <div className="row">
        <h1>Obligations</h1>
        {canConfigure && (
          <button type="button" onClick={() => setEditing('new')}>
            New obligation
          </button>
        )}
      </div>
      <p className="notice">
        An obligation is a dated, versioned statement of what participants must do, linked to the device rule set that gives it effect. Publishing a new version closes the
        open one the day the new one takes effect; earlier versions are kept, and each audit finding names the version in force during the audit period.
      </p>
      <ProblemAlert error={obligations.error} />
      {editing && <ObligationEditor key={editing === 'new' ? 'new' : editing.id} base={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />}
      {[...byCode.entries()].map(([code, versions]) => {
        const latest = versions[0]!;
        return (
          <section className="card" key={code}>
            <div className="row">
              <h2>
                {latest.title} <code>{code}</code>
              </h2>
              {canConfigure && (
                <button type="button" className="linklike" onClick={() => setEditing(latest)}>
                  New version from v{latest.version}
                </button>
              )}
            </div>
            <table className="table">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>In force</th>
                  <th>Title and text</th>
                  <th>Rule set</th>
                  <th>Published</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {versions.map((o) => (
                  <tr key={o.id}>
                    <td>v{o.version}</td>
                    <td>
                      {o.validFrom} → {o.validTo ?? <em>open</em>}
                    </td>
                    <td>
                      <strong>{o.title}</strong>
                      <p style={{ margin: 0 }}>{o.text}</p>
                    </td>
                    <td>{o.ruleSet ? ruleLabel(o.ruleSet) : <span className="muted">none</span>}</td>
                    <td>
                      {formatDateTime(o.createdAt)} <span className="muted">{o.createdByName ?? 'seed'}</span>
                    </td>
                    <td>{o.changeReason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        );
      })}
      {obligations.data && byCode.size === 0 && <p className="muted">No obligations yet.</p>}
    </>
  );
}

function ObligationEditor({ base, onDone }: { base: Obligation | null; onDone: () => void }) {
  const qc = useQueryClient();
  const rules = useQuery({ queryKey: ['rules', 'all'], queryFn: rulesApi.list });
  const [code, setCode] = useState(base?.code ?? '');
  const [title, setTitle] = useState(base?.title ?? '');
  const [text, setText] = useState(base?.text ?? '');
  const [validFrom, setValidFrom] = useState('');
  const [ruleSetId, setRuleSetId] = useState(base?.ruleSet ? String(base.ruleSet.id) : '');
  const [changeReason, setChangeReason] = useState('');
  const publish = useMutation({
    mutationFn: obligationsApi.publish,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['obligations'] });
      onDone();
    },
  });
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    publish.mutate({ code, title, text, validFrom, ruleSetId: ruleSetId ? Number(ruleSetId) : null, changeReason });
  };
  const ruleSets: DeviceClassRule[] = [...(rules.data?.items ?? [])].sort((x, y) => x.deviceClass.localeCompare(y.deviceClass) || x.version - y.version);
  return (
    <form className="form card" onSubmit={onSubmit}>
      <h2>{base ? `New version of ${base.code}` : 'New obligation'}</h2>
      <Field label="Code">
        <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} disabled={!!base} placeholder="OBL-BAT-GRID" required />
      </Field>
      <Field label="Title">
        <input value={title} onChange={(e) => setTitle(e.target.value)} required />
      </Field>
      <Field label="Obligation text">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} required />
      </Field>
      <Field label="Valid from" hint={base ? `Must be after ${base.validFrom}` : undefined}>
        <input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} required />
      </Field>
      <Field label="Linked rule set" hint="The device rule set version that gives this obligation effect.">
        <select value={ruleSetId} onChange={(e) => setRuleSetId(e.target.value)}>
          <option value="">None</option>
          {ruleSets.map((r) => (
            <option key={r.id} value={r.id}>
              {ruleLabel(r)}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Reason for change">
        <input value={changeReason} onChange={(e) => setChangeReason(e.target.value)} required />
      </Field>
      <ProblemAlert error={rules.error ?? publish.error} />
      <div className="row">
        <button type="submit" disabled={publish.isPending}>
          Publish
        </button>
        <button type="button" className="linklike" onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  );
}
